import { useState } from 'react'
import { Panel, Button, Field, FormRow, Select, Chip, Banner, ConfirmDialog, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, del } from '../../lib/api'
import { num } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'

type Group = {
  id: string; name: string; fb_ref: string | null; reps: number; joined_at: string | null
  my_leads_30d: number; from_my_alerts: boolean; mine: boolean; full: boolean
}
type Added = { group: { id: string; name: string }; created: boolean; already: boolean; released: number }

/** A rep's own groups: add one when they join it on Facebook, take it off when they leave. */
export default function MyGroups() {
  const { data, error, reload } = useApi<{ groups: Group[]; max_reps: number; viewing_as: boolean }>('/api/my/groups')
  const toast = useToast()
  const [pick, setPick] = useState('')
  const [f, setF] = useState({ name: '', link: '' })
  const [err, setErr] = useState<string | null>(null)
  const [leaving, setLeaving] = useState<Group | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const locked = data.viewing_as
  const mine = data.groups.filter((g) => g.mine)
  const others = data.groups.filter((g) => !g.mine)
  const seen = others.filter((g) => g.from_my_alerts && !g.full)

  const add = async (body: { group_id: string } | { name: string; link: string }, key: string) => {
    setBusy(key); setErr(null)
    try {
      const r = await post<Added>('/api/my/groups', body)
      toast(r.already ? `You are already in ${r.group.name}` : `Added ${r.group.name}${r.released ? `. ${r.released} recent post${r.released === 1 ? '' : 's'} sent to screening.` : ''}`, 'pos')
      setPick(''); setF({ name: '', link: '' }); reload()
    } catch (e) {
      if (key === 'new') setErr((e as Error).message); else toast((e as Error).message, 'neg')
    } finally { setBusy(null) }
  }
  const leave = async (g: Group) => {
    setLeaving(null); setBusy(`leave-${g.id}`)
    try { await del(`/api/my/groups/${g.id}`); toast(`Left ${g.name}`, 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }

  return (
    <>
      {locked && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="These are someone else's groups">You are viewing as them, so changes here are off.</Banner></div>}
      {seen.length > 0 && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title="Your Muse is sending alerts from groups you have not added">
            <div className="stack-tight">
              <span>Add them so their leads come to you.</span>
              <div className="row-tight">
                {seen.map((g) => <Button key={g.id} variant="secondary" onClick={() => add({ group_id: g.id }, g.id)} disabled={locked || busy !== null}>{busy === g.id ? 'Adding' : `Add ${g.name}`}</Button>)}
              </div>
            </div>
          </Banner>
        </div>
      )}

      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Groups you work</h2><Chip>{mine.length}</Chip></div>
          {/* A list, not a table: reps use this on a phone, and Leave must stay on screen. */}
          {mine.length ? (
            <ul className="unmatched-list my-groups">
              {mine.map((g) => (
                <li key={g.id}>
                  <span className="code-meta">
                    <strong>{g.name}</strong>
                    <span className="muted">{g.fb_ref ? `facebook.com/groups/${g.fb_ref}` : 'no link yet'}</span>
                    <span className="muted">{g.reps} of {data.max_reps} reps, {num(g.my_leads_30d)} lead{g.my_leads_30d === 1 ? '' : 's'} for you in 30 days</span>
                  </span>
                  <Button variant="ghost" onClick={() => setLeaving(g)} disabled={locked || busy !== null}>{busy === `leave-${g.id}` ? 'Leaving' : 'Leave'}</Button>
                </li>
              ))}
            </ul>
          ) : <p className="muted" style={{ margin: 0 }}>None yet. Add each group you have joined on Facebook.</p>}
        </Panel>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">Add a group</h2></div>
          <div className="stack-tight">
            <p className="mailbox-note" style={{ margin: 0 }}>Join the group on Facebook first, with the account your Muse watches, and add it to your Muse prompt too. Once it is here, its leads come to you on My leads.</p>
            <FormRow label="A group already on the list" help={`A group can have ${data.max_reps} reps at most.`}>
              <div className="row-tight">
                <Select aria-label="Choose a group" value={pick} onChange={(e) => setPick(e.target.value)} disabled={locked}>
                  <option value="">Choose a group</option>
                  {others.map((g) => <option key={g.id} value={g.id} disabled={g.full}>{g.name}{g.full ? ' (full)' : ''}</option>)}
                </Select>
                <Button onClick={() => add({ group_id: pick }, 'pick')} disabled={locked || !pick || busy !== null}>{busy === 'pick' ? 'Adding' : 'Add'}</Button>
              </div>
            </FormRow>
            <div className="s-label muted">Not on the list?</div>
            <Field label="Group name, exactly as Facebook shows it" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} disabled={locked} />
            <FormRow label="Group link" help="Optional. Copy it from the group's page: facebook.com/groups/...">
              <input className="field" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="https://www.facebook.com/groups/..." disabled={locked} />
            </FormRow>
            {err && <p className="form-error" style={{ margin: 0 }}>{err}</p>}
            <div><Button variant="secondary" onClick={() => add({ name: f.name, link: f.link }, 'new')} disabled={locked || !f.name.trim() || busy !== null}>{busy === 'new' ? 'Adding' : 'Add new group'}</Button></div>
          </div>
        </Panel>
      </div>

      {leaving && (
        <ConfirmDialog title={`Leave ${leaving.name}?`} tone="neg" confirmLabel="Leave group"
                       body="New leads from this group stop coming to you. Leads you already have stay. Leave the group on Facebook too, and take it out of your Muse prompt."
                       onConfirm={() => leave(leaving)} onCancel={() => setLeaving(null)} />
      )}
    </>
  )
}
